import React, { Component } from 'react'
import isEqual from 'lodash/isEqual'
import { withGraphQLClient } from 'components/GraphQLProvider'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { logger } from 'logger'
import { dataFetched } from './reducer'
import { periodicRenewToken } from 'data/auth/actions'
import { selectors as AuthSelectors } from 'data/auth'

import { selectors as UISelectors } from 'data/ui'

const connectivityFromRedux = connect(state => ({
  connectivity: UISelectors.getConnectionStatus(state, 'ping'),
}))

const subscriptionConnectivityHoc = connect(state => ({
  subscriptionsConnectivity: UISelectors.getConnectionStatus(state, 'graphql'),
}))

const tokenRenewalOrSignOutHoc = connect(
  state => ({
    authTokens: AuthSelectors.getAuthTokens(state),
  }),
  { renewTokensOrSignout: periodicRenewToken }
)

export function makesQueries({
  query,
  fetchPolicy = 'network-only',
  resyncPolicy = 'none',
  mapPropsToVariables = x => ({}),
  getOnQueryFromProps = props => _ => {},
  getOnResultFromProps = props => _ => {},
  getOnErrorFromProps = props => _ => {},
  clientGetterHOC = withGraphQLClient,
  connectivityHOC = connectivityFromRedux,
  queryOnChanges = true,
  queryOnMount = true,
  batch = true,
  queryPropName = 'forceQuery',
}) {
  return function wrapMakesQueries(OriginalComponent) {
    class MakesQueries extends Component {
      oldVariables = undefined

      render() {
        const props = {
          ...this.props,
          [queryPropName]: this.forceQuery,
        }

        return <OriginalComponent {...props} />
      }

      componentDidMount() {
        if (!queryOnMount) {
          return
        }

        queryOnChanges && this.makeQueryIfNeeded(null, this.props)
      }

      componentWillUnmount() {
        clearTimeout(this.refreshHandle)
      }

      componentWillReceiveProps(newProps) {
        this.makeQueryIfNeeded(this.props, newProps)
      }

      makeQueryIfNeeded(oldProps, newProps) {
        const newVariables = mapPropsToVariables(newProps)
        // In the future, store disconnectedAt and inject it as a variable when refetching
        if (
          oldProps &&
          oldProps.connectivity ===
            UISelectors.CONNECTION_STATUSES_TYPE.DISCONNECTED &&
          newProps.connectivity ===
            UISelectors.CONNECTION_STATUSES_TYPE.CONNECTED
        ) {
          logger.graphql('Reconnection detected', {
            query: oldProps.query,
            newVariables,
          })
          this.applyResyncPolicy(newVariables)
          return
        }

        if (!queryOnChanges) {
          return
        }

        const oldVariables = this.oldVariables
        this.oldVariables = newVariables // save next call

        if (!newVariables || isEqual(newVariables, oldVariables)) {
          return
        }

        this.makeQuery(newVariables, newProps)
      }

      async makeQuery(variables, props, isRefresh) {
        const meta = { query, variables, isRefresh }
        const { renewTokensOrSignout } = props

        getOnQueryFromProps(props)({ meta })

        return props.graphQLClient
          .query({ query, variables, fetchPolicy, batch })
          .then(
            result => {
              getOnResultFromProps(props)({ result, meta })
            },
            data => {
              const userIsLoggedOut =
                data.errors &&
                data.errors.find(n => n.id === 'auth/unauthorized/no-anonymous')

              if (!data.errors && window.bugsnag) {
                window.bugsnag.notify(
                  new Error('errors not provided in GQL error callback'),
                  {
                    metaData: {
                      errorMessage: data.message,
                      query,
                      variables,
                      fetchPolicy,
                    },
                  }
                )
              }

              if (data.message !== 'Failed to fetch') {
                //Ignoring apollo network errors here, to not make the app crash
                getOnErrorFromProps(props)({ error: data, meta })
              }

              if (userIsLoggedOut) {
                renewTokensOrSignout({ tokens: props.authTokens })
              }
            }
          )
      }

      forceQuery = () => {
        this.makeQuery(mapPropsToVariables(this.props), this.props)
      }

      applyResyncPolicy(variables) {
        switch (resyncPolicy) {
          case 'refetch':
            clearTimeout(this.refreshHandle)
            // between 10 and 40 seconds
            const delay = Math.floor((Math.random() * 30 + 10) * 1000)
            this.refreshHandle = setTimeout(() => {
              logger.graphql('Refetching...')
              this.makeQuery(variables, this.props, true)
            }, delay)
            break
          default:
            return false
        }
      }
    }

    return compose(
      clientGetterHOC,
      connectivityHOC,
      tokenRenewalOrSignOutHoc
    )(MakesQueries)
  }
}

export function makesPaginatedQueries({
  query,
  fetchPolicy = 'network-only',
  resetOnUnmount = false,
  mapControlsToProps = ({ reset, fetchNextPage }) => ({ reset, fetchNextPage }),
  getVariables = props => _ => {},
  getOnReset = props => _ => {},
  shouldReset = (props, newProps) => false,
  getOnFetchSuccess = props => _ => {},
  getOnFetchStart = props => _ => {},
  getOnFetchError = props => _ => {},
  clientGetterHOC = withGraphQLClient,
  batch = true,
}) {
  return function wrapMakesPaginatedQueries(OriginalComponent) {
    class MakesPaginatedQueries extends Component {
      render() {
        const { props, reset, fetchNextPage } = this
        const controlsProps = mapControlsToProps({ reset, fetchNextPage })
        return <OriginalComponent {...props} {...controlsProps} />
      }

      componentWillUnmount() {
        if (resetOnUnmount) {
          this.reset()
        }
      }

      componentWillReceiveProps(newProps) {
        if (shouldReset(this.props, newProps)) {
          this.reset()
        }
      }

      reset = () => {
        getOnReset(this.props)()
      }

      fetchNextPage = (options = {}) => {
        const onNextPage = getOnFetchSuccess(this.props)
        const variables = getVariables(this.props)({ options })

        const meta = { query, variables }
        getOnFetchStart(this.props)({ options, meta })

        return this.props.graphQLClient
          .query({ query, variables, fetchPolicy, batch })
          .then(
            result => onNextPage({ result, meta, options }),
            error => getOnFetchError(this.props)({ error, meta, options })
          )
      }
    }

    return clientGetterHOC(MakesPaginatedQueries)
  }
}

/**
 * @description Fetches a full list of a paginated resource.
 * The sole purpose of this is to be able to load a list fully for now, until we have a better
 * UX for this.
 * Examples of usage:
 * - Loading the full list of users at app startup time so it is available subsequently.
 *
 * ⚠️ this *will* load a list until every item has been queried from the backend.
 * It's *NOT* supposed to be used everywhere, but only as a temporary solution for big lists loading issues.
 *
 * 🧐 Beware!
 */
export function makesIntegralFetchPaginatedQuery({
  query,
  batch = false,
  fetchPolicy = 'network-only',
  resetOnUnmount = false,
  mapControlsToProps = ({ reset, fetchNextPage }) => ({ reset, fetchNextPage }),
  getVariables = props => _ => {},
  getOnReset = props => _ => {},
  shouldReset = (props, newProps) => false,
  getShouldContinueLoading = props => _ => {},
  getOnFetchStart = props => _ => {},
  getOnFetchError = props => _ => {},
  getOnFetchSuccess = props => _ => {},
  clientGetterHOC = withGraphQLClient,
}) {
  return function wrapMakesPaginatedQueries(OriginalComponent) {
    class MakesPaginatedQueries extends Component {
      render() {
        const { props, reset, fetchNextPage } = this
        const controlsProps = mapControlsToProps({ reset, fetchNextPage })
        return <OriginalComponent {...props} {...controlsProps} />
      }

      componentWillUnmount() {
        if (resetOnUnmount) {
          this.reset()
        }
      }

      componentWillReceiveProps(newProps) {
        if (shouldReset(this.props, newProps)) {
          this.reset()
        }
      }

      componentDidMount() {
        this.fetchNextPage()
      }

      reset = () => {
        getOnReset(this.props)()
      }

      fetchNextPage = (options = {}) => {
        const onNextPage = getOnFetchSuccess(this.props)
        const variables = getVariables(this.props)({ options })
        const shouldContinueLoading = getShouldContinueLoading(this.props)

        const meta = { query, variables }
        getOnFetchStart(this.props)({ options, meta })

        return this.props.graphQLClient
          .query({ query, variables, fetchPolicy, batch })
          .then(
            result => {
              onNextPage({ result, meta, options })

              if (shouldContinueLoading({ result })) {
                return this.fetchNextPage(options)
              }
            },
            error => getOnFetchError(this.props)({ error, meta, options })
          )
      }
    }

    return clientGetterHOC(MakesPaginatedQueries)
  }
}

export function makesSubscriptions({
  query,
  mapPropsToVariables = x => x,
  getOnQueryFromProps = props => _ => {},
  getOnResultFromProps = props => _ => {},
  getOnErrorFromProps = props => error => {
    logger.error('Subscription error', error)
  },
  clientGetterHOC = withGraphQLClient,
  subscriptionStatusHoc = subscriptionConnectivityHoc,
}) {
  return function wrapMakesSubscriptions(OriginalComponent) {
    class MakesSubscriptions extends Component {
      oldVariables = undefined
      querySubscribe = undefined

      render() {
        return <OriginalComponent {...this.props} />
      }

      componentDidMount() {
        this.makeSubscriptionIfNeeded(this.props)
      }

      componentWillReceiveProps(newProps) {
        this.makeSubscriptionIfNeeded(newProps)
      }

      componentWillUnmount() {
        this.unsubscribe(this.querySubscribe)
      }

      makeSubscriptionIfNeeded(newProps) {
        const newVariables = mapPropsToVariables(newProps)
        const oldVariables = this.oldVariables
        this.oldVariables = newVariables // save next call

        const isReconnecting =
          this.props &&
          this.props.subscriptionsConnectivity ===
            UISelectors.CONNECTION_STATUSES_TYPE.DISCONNECTED &&
          newProps.subscriptionsConnectivity ===
            UISelectors.CONNECTION_STATUSES_TYPE.CONNECTED

        if (isReconnecting === false && isEqual(newVariables, oldVariables)) {
          return
        }

        this.makeQuery(newVariables, newProps)
      }

      makeQuery(variables = {}, props = this.props) {
        const meta = { query, variables }
        getOnQueryFromProps(props)({ meta })

        // save the old query subscribe for later unsubscribe
        const previousQuerySubscribe = this.querySubscribe
        const next = result => {
          if (result.errors) {
            return getOnErrorFromProps(props)({ error: result.errors, meta })
          }
          getOnResultFromProps(props)({ result, meta })
        }
        const error = error => getOnErrorFromProps(props)({ error, meta })

        const queryObservable = this.props.graphQLClient.subscribe({
          query,
          variables,
        })
        this.querySubscribe = queryObservable.subscribe({ next, error })
        this.unsubscribe(previousQuerySubscribe)
      }

      unsubscribe(querySubscribe) {
        if (querySubscribe) {
          querySubscribe.unsubscribe()
        }
      }
    }

    return compose(
      clientGetterHOC,
      subscriptionStatusHoc
    )(MakesSubscriptions)
  }
}

export function makesMutations({
  query,
  defaultVariables = {},
  mutationPropName = 'makeMutation',
  getOnQueryFromProps = props => _ => {},
  getOnResultFromProps = props => _ => {},
  getOnErrorFromProps = props => error => {
    throw error
  },
  clientGetterHOC = withGraphQLClient,
  batch = true,
}) {
  return function wrapMakesMutations(OriginalComponent) {
    class MakesMutations extends Component {
      render() {
        const props = {
          ...this.props,
          [mutationPropName]: this.makeMutation,
        }
        return <OriginalComponent {...props} />
      }

      makeMutation = callVariables => {
        const variables = { ...defaultVariables, ...callVariables }
        const meta = { query, variables }
        getOnQueryFromProps(this.props)({ meta })

        return this.props.graphQLClient
          .mutate({ query, variables, batch })
          .then(
            result => {
              getOnResultFromProps(this.props)({ result, meta })
              return { result, meta }
            },
            error => {
              getOnErrorFromProps(this.props)({ variables, error, meta })
              return { error, meta }
            }
          )
      }
    }

    return clientGetterHOC(MakesMutations)
  }
}

export const connectDataFetched = connect(
  null,
  { dataFetched }
)

export const extraDataFromResultAndDispatch = name => ({ dataFetched }) => ({
  result,
  meta,
}) => {
  const data = result.data[name]
  dataFetched(data)
}
